import { datadogRum } from "@datadog/browser-rum";

import { ResultCodes } from "@app/service/resultCodes";

export interface GraphQLErrorBody {
  code: string;
  message: string;
}

type GraphQLErrorLike = {
  message?: string;
  extensions?: unknown;
};

const APOLLO_INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR";
const UNKNOWN_ERROR_MESSAGE = "Unknown error";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const reportUnexpectedGraphQLError = (error: unknown, reason: string): void => {
  if (!datadogRum.getInitConfiguration()) return;

  const err = error instanceof Error ? error : new Error(reason);
  datadogRum.addError(err, {
    source: "graphql-error-code",
    reason,
  });
};

// Duck-typed rather than `instanceof ApolloError` on purpose: callers hand us
// anything from a real ApolloError to a plain rejected object, and the shape
// is all we need.
const getGraphQLErrors = (error?: unknown): GraphQLErrorLike[] => {
  const graphQLErrors = (error as { graphQLErrors?: unknown } | undefined)
    ?.graphQLErrors;
  if (!Array.isArray(graphQLErrors)) return [];

  return graphQLErrors.filter((err): err is GraphQLErrorLike => isObject(err));
};

const readCode = (graphQLError?: GraphQLErrorLike): string | undefined => {
  const extensions = graphQLError?.extensions;
  if (!isObject(extensions)) return undefined;

  // Narrow to string primitives — guards against malformed error payloads
  // (non-string code) producing surprising === comparisons downstream.
  const direct = readString(extensions.code);
  const legacy = isObject(extensions.exception)
    ? readString(extensions.exception.code)
    : undefined;

  // Old servers leave the app ResultCode on exception.code and Apollo's
  // INTERNAL_SERVER_ERROR on extensions.code. Prefer the legacy code in that
  // case so a new client still works before the server rollout finishes.
  if (legacy && (!direct || direct === APOLLO_INTERNAL_SERVER_ERROR)) {
    return legacy;
  }
  return direct;
};

/**
 * Reads the app-level ResultCode off the first GraphQL error. The server
 * promotes ApplicationError.code onto extensions.code and, during rollout,
 * extensions.exception.code. A GraphQL error with no readable code becomes
 * UNCAUGHT_ERROR so callers that treat the string as the error itself still
 * surface a generic failure. Undefined input and network-only errors stay
 * undefined.
 */
export const getGraphQLErrorCode = (error?: unknown): string | undefined => {
  try {
    const graphQLErrors = getGraphQLErrors(error);
    const code = readCode(graphQLErrors[0]);
    if (code) return code;
    if (graphQLErrors.length > 0) return ResultCodes.UNCAUGHT_ERROR;
    return undefined;
  } catch (caught) {
    reportUnexpectedGraphQLError(caught, "graphql-error-code-threw");
    return undefined;
  }
};

/** Code plus user-facing message for every GraphQL error on the response. */
export const getGraphQLErrorBodies = (error?: unknown): GraphQLErrorBody[] => {
  try {
    return getGraphQLErrors(error).map((graphQLError) => ({
      code: readCode(graphQLError) ?? "",
      message:
        typeof graphQLError.message === "string" ? graphQLError.message : "",
    }));
  } catch (caught) {
    reportUnexpectedGraphQLError(caught, "graphql-error-bodies-threw");
    return [];
  }
};

/**
 * First GraphQL error body, or a synthetic UNCAUGHT_ERROR when the response
 * has no readable body. Callers that throw the result must not throw undefined.
 */
export const requireGraphQLErrorBody = (error?: unknown): GraphQLErrorBody => {
  const body = getGraphQLErrorBodies(error)[0];
  if (body) return body;

  reportUnexpectedGraphQLError(error, "graphql-error-body-missing");
  return { code: ResultCodes.UNCAUGHT_ERROR, message: UNKNOWN_ERROR_MESSAGE };
};
